import React, { useMemo, useState } from 'react';
import { ReceiptIcon, Trash2Icon, XIcon } from 'lucide-react';
import { CompanySettings, ProInvoice, ProInvoiceStatus } from '../../types/pro';
import { INVOICE_STATUS, formatDate, kr, krPrecise, lineTotal, newLineId, totals } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { SidePanel } from '../../components/pro/SidePanel';
interface ProInvoicesProps {
  invoices: ProInvoice[];
  company: CompanySettings;
  onUpdate: (id: string, changes: Partial<ProInvoice>) => void;
}
export function ProInvoices({
  invoices,
  company,
  onUpdate
}: ProInvoicesProps) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<ProInvoiceStatus | 'alle'>('alle');
  const [openId, setOpenId] = useState<string | null>(null);
  const open = invoices.find(invoice => invoice.id === openId) ?? null;
  const sums = open ? totals(open.lines, company.vatRate) : null;
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return invoices.filter(invoice => status === 'alle' || invoice.status === status).filter(invoice => term ? [invoice.invoiceNo, invoice.customerName].join(' ').toLowerCase().includes(term) : true).sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate));
  }, [invoices, query, status]);
  const overdue = invoices.filter(i => i.status === 'forfalden');
  const outstanding = invoices.filter(i => ['sendt', 'forfalden'].includes(i.status)).reduce((sum, i) => sum + totals(i.lines, company.vatRate).total, 0);
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500';
  return <div data-id="element-918" data-mp="1ci3j54bubwfr">
      <PageHeader title="Fakturaer" subtitle={`${kr(outstanding)} udestående${overdue.length > 0 ? ` · ${overdue.length} forfaldne` : ''}`} data-id="element-919" data-mp="1had1nzamlu80" />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-920" data-mp="13hf200r5hfyn">
        <SearchField value={query} onChange={setQuery} placeholder="Søg fakturanummer eller kunde…" className="min-w-[220px] flex-1" data-id="element-921" data-mp="17ld0qackcor6" />
        <label data-id="element-922" data-mp="208fy2z5i6tfu">
          <span className="sr-only" data-id="element-923" data-mp="208z3xhhvprdi">Filtrer status</span>
          <select value={status} onChange={e => setStatus(e.target.value as ProInvoiceStatus | 'alle')} className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-id="element-924" data-mp="1ma96u1v27rp5">
            <option value="alle" data-id="element-925" data-mp="fu6xhl1jqaxo">Alle statusser</option>
            {(Object.keys(INVOICE_STATUS) as ProInvoiceStatus[]).map(key => <option key={key} value={key} data-id="element-926" data-mp="7b292752ieed">
                {INVOICE_STATUS[key].label}
              </option>)}
          </select>
        </label>
      </div>

      {rows.length === 0 ? <EmptyState icon={ReceiptIcon} title="Ingen fakturaer matcher" data-id="element-927" data-mp="haazwr4mzcwi" /> : <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-928" data-mp="cd1cti4pzdbu">
          <table className="w-full min-w-[680px] text-sm" data-id="element-929" data-mp="3ksl1e3ug47kk">
            <thead data-id="element-930" data-mp="3fsgk4blpr8ta">
              <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-931" data-mp="3fsymzr2vsla6">
                {['Nummer', 'Kunde', 'Beløb', 'Fakturadato', 'Forfaldsdato', 'Status'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-932" data-mp="3200bmva1mikz">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-933" data-mp="25a6kldyprh2n">
              {rows.map(invoice => {
            const isOverdue = invoice.status === 'forfalden';
            return <tr key={invoice.id} onClick={() => setOpenId(invoice.id ?? null)} className={`cursor-pointer transition-colors hover:bg-stone-50 ${isOverdue ? 'bg-red-50/40' : ''}`} data-id="element-934" data-mp="2e2s10nevhbob">
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-stone-500" data-id="element-935" data-mp="2szr4q06lskdi">
                      {invoice.invoiceNo}
                    </td>
                    <td className="px-4 py-3 font-medium text-stone-900" data-id="element-936" data-mp="ym005f34sqox">
                      {invoice.customerName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums text-stone-900" data-id="element-937" data-mp="12n3efx5j6eo1">
                      {kr(totals(invoice.lines, company.vatRate).total)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-stone-500" data-id="element-938" data-mp="qahgtuw9cb6r">
                      {formatDate(invoice.invoiceDate)}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-3 ${isOverdue ? 'font-semibold text-red-600' : 'text-stone-500'}`} data-id="element-939" data-mp="hcxd8faxa2sz">
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="px-4 py-3" data-id="element-940" data-mp="153b9yj4gcptw">
                      <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-941" data-mp="101c6784svqwf" />
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}

      <SidePanel isOpen={Boolean(open)} onClose={() => setOpenId(null)} title={open?.invoiceNo ?? ''} subtitle={open?.customerName} footer={open && <div className="flex gap-2" data-id="element-943" data-mp="3r8csiijcqzqx">
              {(['kladde', 'sendt', 'betalt', 'forfalden'] as ProInvoiceStatus[]).map(option => <button key={option} onClick={() => open.id && onUpdate(open.id, {
        status: option
      })} aria-pressed={open.status === option} className={`flex-1 rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${open.status === option ? 'bg-stone-900 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-944" data-mp="3ha5n8wyfe64r">
                    {INVOICE_STATUS[option].label}
                  </button>)}
            </div>} data-id="element-942" data-mp="3krtfutra16ec">
        {open && sums && <div className="space-y-5" data-id="element-945" data-mp="25ztnsn7w5sg6">
            <div className="grid grid-cols-2 gap-3" data-id="element-946" data-mp="20z5xy4vefhnj">
              <label className="block" data-id="element-947" data-mp="210h4udk555iv">
                <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-948" data-mp="2eyx79saz09v7">
                  Fakturadato
                </span>
                <input type="date" value={open.invoiceDate} onChange={e => open.id && onUpdate(open.id, {
              invoiceDate: e.target.value
            })} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-id="element-949" data-mp="2aywuec3is6q9" />
              </label>
              <label className="block" data-id="element-950" data-mp="2tpnyoz8nh6xs">
                <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-951" data-mp="37mb9o5xftxmp">
                  Forfaldsdato
                </span>
                <input type="date" value={open.dueDate} onChange={e => open.id && onUpdate(open.id, {
              dueDate: e.target.value
            })} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-id="element-952" data-mp="32m5dccg83mya" />
              </label>
            </div>

            <div data-id="element-953" data-mp="3ra7p220p9wh6">
              <div className="mb-2 flex items-center justify-between" data-id="element-954" data-mp="3raqvomkwgyfw">
                <h3 className="text-sm font-semibold text-stone-900" data-id="element-955" data-mp="3vbj7nnl5phfv">
                  Fakturalinjer
                </h3>
                <button onClick={() => open.id && onUpdate(open.id, {
              lines: [...open.lines, {
                id: newLineId(),
                description: '',
                quantity: 1,
                unit: 'stk',
                unitPrice: 0
              }]
            })} className="rounded-lg bg-stone-900 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-956" data-mp="44zvo53l3lfz">
                  Ny linje
                </button>
              </div>

              <ul className="divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200" data-id="element-957" data-mp="2r3u2vhmv0f5s">
                {open.lines.map(line => <li key={line.id} className="flex items-center gap-1 p-1.5" data-id="element-958" data-mp="2hawtq23rc2ju">
                    <input value={line.description} onChange={e => open.id && onUpdate(open.id, {
                lines: open.lines.map(l => l.id === line.id ? {
                  ...l,
                  description: e.target.value
                } : l)
              })} placeholder="Beskrivelse" aria-label="Beskrivelse" className={`${cell} flex-1`} data-id="element-959" data-mp="2ccbrxy8uxata" />
                    <input type="number" value={line.quantity} onChange={e => open.id && onUpdate(open.id, {
                lines: open.lines.map(l => l.id === line.id ? {
                  ...l,
                  quantity: parseFloat(e.target.value) || 0
                } : l)
              })} aria-label="Antal" className={`${cell} w-16 text-right tabular-nums`} data-id="element-960" data-mp="p5aw3bn8ftsa" />
                    <input type="number" value={line.unitPrice} onChange={e => open.id && onUpdate(open.id, {
                lines: open.lines.map(l => l.id === line.id ? {
                  ...l,
                  unitPrice: parseFloat(e.target.value) || 0
                } : l)
              })} aria-label="Stykpris" className={`${cell} w-24 text-right tabular-nums`} data-id="element-961" data-mp="1keecv6xagou7" />
                    <button onClick={() => open.id && onUpdate(open.id, {
                lines: open.lines.filter(l => l.id !== line.id)
              })} aria-label="Slet linje" className="rounded p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-962" data-mp="1yccgpidzh2vg">
                      <XIcon className="h-4 w-4" data-id="element-963" data-mp="2rp0jg1endho6" />
                    </button>
                  </li>)}
                {open.lines.length === 0 && <li className="px-4 py-8 text-center text-sm text-stone-400" data-id="element-964" data-mp="2hsntnem83nyp">
                    Ingen linjer endnu
                  </li>}
              </ul>
            </div>

            <dl className="space-y-2 rounded-xl border border-stone-200 bg-stone-50 p-4 text-sm" data-id="element-965" data-mp="28zkg76bkvb42">
              <div className="flex justify-between" data-id="element-966" data-mp="240zet3klale1">
                <dt className="text-stone-500" data-id="element-967" data-mp="242kd94a0yoh6">Subtotal</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-968" data-mp="1z1wcvrzwovaa">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-969" data-mp="1u4mq6k3wixq5">
                <dt className="text-stone-500" data-id="element-970" data-mp="1jzedkcl39cuh">
                  Moms {Math.round(company.vatRate * 100)}%
                </dt>
                <dd className="tabular-nums text-stone-900" data-id="element-971" data-mp="1ow4u110t2agf">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-stone-200 pt-2 text-base font-bold" data-id="element-972" data-mp="1yvvs1cokr7l9">
                <dt className="text-stone-900" data-id="element-973" data-mp="118dja1uwy5h9">Total</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-974" data-mp="119ydfqdbfmel">
                  {krPrecise(sums.total)}
                </dd>
              </div>
            </dl>
          </div>}
      </SidePanel>
    </div>;
}